import "react-datepicker/dist/react-datepicker.css";
import { useEffect, useMemo, useState } from "react";
import { ReportApi } from "../../../services/api/ReportApi";
import {
  ColDef,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  LoadSuccessParams,
  GridApi,
  ColGroupDef,
  ValueFormatterParams,
  SizeColumnsToContentStrategy,
} from "ag-grid-enterprise";
import useSidebar from "../../../hooks/useSidebar";
import BaseGrid from "../../BaseGrid";
import { downloadFile } from "../../../utils/DownloadFile";
import useReport from "../../../hooks/useReport";
import LoadingComponent from "../../../components/LoadingComponent";
import { MasterCardSettlementReconciliationModel } from "../../../models/MasterCardSettlementReconciliationModel";
import SearchCriteriaRedReports from "../../../components/SearchCriteriaRedReports";
import { formatCurrency } from "../../../utils/FormatCurrency";
import {
  AgBarSeriesOptions,
  AgChartOptions,
} from "ag-charts-enterprise";
import { MasterCardSettlementReconciliationSummaryModel } from "../../../models/MasterCardSettlementReconciliationSummaryModel";
import { DiscrepancyAmountByDate } from "../../../models/DiscrepancyAmountByDate";
import useTheme from "../../../hooks/useTheme";
import { AgCharts } from "ag-charts-react";

const MasterCardSettlementReconciliation = () => {
  const { setIsOpen } = useSidebar();
  const { theme } = useTheme();
  const { reportInputs, mapReportRequest, getContextMenuItems } = useReport();

  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [gridReady, setGridReady] = useState(false);
  const [dataSource, setDataSource] = useState<IServerSideDatasource>();
  const [gridApi, setGridApi] = useState<GridApi>();
  const [isDownload, setIsDownload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const nextOptions = { ...options };
    nextOptions.theme = theme === "dark" ? "ag-material-dark" : "ag-material";
    setOptions(nextOptions);
  }, [theme]);

  const handleViewReport = () => {
    setIsDownload(false);
    setGridReady(true);
    setDataSource(createServerSideDatasource());
  };

  const handleDownloadReport = () => {
    setIsDownload(true);
    setIsLoading(true);
    ReportApi.exportMasterCardSettlementReconciliation(mapReportRequest(null))
      .then((res) => {
        downloadFile(MasterCardSettlementReconciliation.displayName, res.data);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const currencyFormatter = (
    params: ValueFormatterParams<MasterCardSettlementReconciliationModel, any>
  ) => {
    if (params.value == undefined || params.data == undefined) return "";
    if (params.value === 0) return "-";
    const alphaCode = params.data?.alphaCode;
    const cur = formatCurrency(params.value, alphaCode);
    if (params.value < 0) {
      const formattedCur = cur + "";
      return "(" + formattedCur.replace("-", "") + ")";
    }
    return cur + "";
  };

  const [colDefs] = useState<
    | ColDef<MasterCardSettlementReconciliationModel>[]
    | ColGroupDef<MasterCardSettlementReconciliationModel>[]
  >([
    {
      headerName: "Files Processed",

      children: [
        {
          headerName: "",
          children: [
            {
              headerName: "ICA",
              field: "ica",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 80,
            },
            {
              headerName: "Currency Code",
              field: "currencyCode",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 80,
            },
            {
              headerName: "Clearing Date",
              field: "gcmsFileReferenceDate",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 80,
            },
            {
              headerName: "GCMS File Id",
              field: "gcmsFileId",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 120,
            },
            {
              headerName: "Clearing Cycle",
              field: "gcmsClearingCycle",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 100,
            },
          ],
        },
      ],
    },
    {
      headerName: "MasterCard Data",
      headerStyle: { textAlign: "center" },
      children: [
        {
          headerName: "Reconciliation",
          children: [
            {
              headerName: "Amount Net Transaction",
              field: "mcAmountNetTransaction",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 120,
            },
            {
              headerName: "Amount Net Fees",
              field: "mcAmountNetFees",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
            {
              headerName: "Amount Net Total",
              field: "mcAmountNetTotal",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
          ],
        },
        {
          headerName: "Presentments",
          children: [
            {
              headerName: "Count",
              field: "mcPresentmentCount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 100,
            },
            {
              headerName: "Amount",
              field: "mcPresentmentAmount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
          ],
        },
        {
          headerName: "Non-Cardholder",
          wrapHeaderText: true,
          autoHeaderHeight: true,
          children: [
            {
              field: "income",
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
            {
              field: "fees",
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
          ],
        },
        {
          headerName: "",
          children: [
            {
              headerName: "Total Settlement",
              field: "mcTotalSettlement",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 120,
            },
          ],
        },
      ],
    },
    {
      headerName: "StoreFinancial System Data",
      children: [
        {
          headerName: "Presentments",
          children: [
            {
              headerName: "Purchase Count",
              field: "sfPurchaseCount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 100,
            },
            {
              headerName: "Purchase Return Count",
              field: "sfPurchaseReturnCount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 100,
            },
            {
              headerName: "ATM Count",
              field: "sfAtmCount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 100,
            },
            {
              headerName: "Chargeback Count",
              field: "sfChargebackCount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 100,
            },
            {
              headerName: "Presentment Count",
              field: "sfPresentmentCount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              minWidth: 100,
            },
            {
              headerName: "Client Programs",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              field: "sfClientPresentmentAmount",
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
            {
              headerName: "Purchase Amount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              field: "sfPurchaseAmount",
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
            {
              headerName: "Purchase Return Amount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              field: "sfPurchaseReturnAmount",
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
            {
              headerName: "ATM Amount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              field: "sfAtmAmount",
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
            {
              headerName: "Chargeback Amount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              field: "sfChargebackAmount",
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
            {
              headerName: "Other Non-Cardholder Settlement Amount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              field: "sfOtherNonCardHolderAmount",
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 120,
            },
            {
              headerName: "Internal Programs",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              field: "sfInternalPresentmentCount",
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
            {
              headerName: "Balance Inquiry",
              field: "sfBalanceInquiryAmount",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
            {
              headerName: "Total Processed",
              field: "sfTotal",
              wrapHeaderText: true,
              autoHeaderHeight: true,
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 100,
            },
          ],
        },
        {
          headerName: "",
          children: [
            {
              field: "discrepancy",
              valueFormatter: currencyFormatter,
              cellClassRules: {
                "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
              },
              minWidth: 80,
            },
          ],
        },
      ],
    },
  ]);

  const defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    cellStyle: { textAlign: "center" },
  };

  const autoSizeStrategy = useMemo<SizeColumnsToContentStrategy>(() => {
    return {
      type: "fitCellContents",
      defaultMinWidth: 60,
      defaultMaxWidth: 150,
      skipHeader: true,
    };
  }, []);

  const createServerSideDatasource: () => IServerSideDatasource = () => {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        getReportData(params);
      },
    };
  };

  const getReportData = (params: IServerSideGetRowsParams | null) => {
    setIsOpen(false);
    ReportApi.getMasterCardSettlementReconciliation(mapReportRequest(params))
      .then((res) => {
        const result = res.data;
        result?.rows.map(
          (x: MasterCardSettlementReconciliationModel) =>
            ((x.gcmsFileReferenceDate = x.gcmsFileReferenceDate
              ? x.gcmsFileId ? new Date(x.gcmsFileReferenceDate).toLocaleDateString() : ""
              : undefined),
              (x.gcmsFileId = x.gcmsFileId ? x.gcmsFileId : "Totals:")),                            
        );
        const successParams: LoadSuccessParams = {
          rowData: result.rows,
          rowCount: result.totalCount,
        };
        if ((result?.rows?.length ?? 0) > 0) {
          params?.api?.hideOverlay();
          params?.success(successParams);
        } else {
          params?.api?.showNoRowsOverlay();
          params?.success({ rowData: [], rowCount: 0 });
        }
        const cd = mapChartData(result.summaryDetail);
        setChartData(cd);
        setOptions((prevOptions) => ({
          ...prevOptions,
          data: cd,
        }));
      })
      .catch(() => {
        params?.fail();
      });
  };

  const mapChartData = (
    data: MasterCardSettlementReconciliationSummaryModel
  ) => {
    const discrepancyAmountsByDate: DiscrepancyAmountByDate[] = [];
    data.discrepancyAmounts.map((ca) => {
      const discrepancyAmount: DiscrepancyAmountByDate = {
        amount: ca.amount,
        date: new Date(ca.date).toLocaleDateString(),
      };
      discrepancyAmountsByDate.push(discrepancyAmount);
    });
    return discrepancyAmountsByDate;
  };

  const [options, setOptions] = useState<AgChartOptions>({
    title: {
      text: "Discrepancy Amount By Date",
    },
    // Data: Data to be displayed in the chart
    data: chartData as ChartData[],
    // Series: Defines which chart type and data to use
    series: [
      {
        type: "bar",
        xKey: "date",
        yKey: "amount",        
      } as AgBarSeriesOptions,
    ],    
    theme: theme === "dark" ? "ag-material-dark" : "ag-material",
  });

  return (
    <>
      <div className="report-container">
        <div className="report-header">
          <h5 className="mb-0">MasterCard Settlement Reconciliation</h5>
          {(reportInputs?.icaValue?.length ?? 0) > 0 &&
            reportInputs?.fromDate &&
            reportInputs?.toDate && (
              <div className="report-header-date">{`${reportInputs?.fromDate.toLocaleDateString()} - ${reportInputs?.toDate.toLocaleDateString()}`}</div>
            )}
        </div>
        <div className="blocks">
          <SearchCriteriaRedReports
            handleViewReport={handleViewReport}
            handleDownloadReport={handleDownloadReport} 
            useIcas={true}           
          />
          {(chartData?.length ?? 0) > 0 && !isDownload && (
            <div className="report-chart">
              <AgCharts options={options} />
            </div>
          )}
        </div>
        {isDownload && isLoading ? (
          <LoadingComponent />
        ) : (
          !isDownload && (
            <BaseGrid
              colDefs={colDefs}
              defaultColDef={defaultColDef}
              getData={getReportData}
              isGridReady={gridReady}
              reportInputs={reportInputs}
              dataSource={dataSource}
              gridApi={gridApi}
              setGridApi={setGridApi}
              getContextMenuItems={getContextMenuItems}
              autoSizeStrategy={autoSizeStrategy}
            />
          )
        )}
      </div>
    </>
  );
};

MasterCardSettlementReconciliation.displayName =
  "MasterCardSettlementReconciliation";

export default MasterCardSettlementReconciliation;
